import { useStore } from 'effector-react'
import React, { FC, Fragment, useEffect, useState } from 'react'
import styled from 'styled-components'

import { compareFiltersLength } from '../../../lib/compare-filter-length'
import { getProjectName } from '../../../lib/get-project-name'
import { setCarsNextDealer } from '../../../lib/show-cars-next-dealer'
import { objectHasProperty } from '../../../lib/typescript-helpers'
import { useAVNConfig } from '../../../stages/AVN'
import { Icon } from '../../../ui/icon'
import { Tooltip } from '../../../ui/tooltip'
import {
  $dealersShowMoreCars,
  $declaration,
  $declarationWithRelatedFilters,
  $filterValues,
  FilterSection,
  removeValues,
  resetValues
} from '../../filter/model'
import { $isLoadMoreCars } from '../model'

type FilterItem = { label: string; key: string; sectionKey: string; tooltip?: string }

const createFilterItem = (key: any, section?: FilterSection) => {
  if (section) {
    for (let i = 0; i <= section.groups.length; i++) {
      if (section.type === 'range') {
        const label = !isNaN(key.value) && key.value % 1 > 0 ? key.value.toFixed(1) : key.value
        if (key.range === 'max') {
          return getObjectForFilter('до: ' + label, key, section.key, '')
        } else {
          return getObjectForFilter('от: ' + label, key, section.key, '')
        }
      } else {
        const item = section?.groups[i]?.fields.find((elem) => {
          return objectHasProperty(elem, 'value') ? elem.value === key : null
        })
        if (item) {
          const label = objectHasProperty(item, 'label') ? item.label : ''
          const colorName = objectHasProperty(item, 'colorName') ? item.colorName : ''
          const labelForSelectedFilter = label || colorName || ''
          // @ts-ignore
          return getObjectForFilter(labelForSelectedFilter, item?.value, section.key, item.tooltip)
        }
      }
    }
  }
  return getObjectForFilter('', '', '', '')
}

const getObjectForFilter = (label: string, key: string, sectionKey: any, tooltip: string) => {
  return {
    label,
    key,
    sectionKey,
    tooltip
  }
}

const getObjectForFilterRange = (range: string, value: string | number, title: string) => {
  const convertValue = returnsConvertsPriceOrReturnsValue(title, value)
  return {
    range,
    value: convertValue,
    title
  }
}

const returnsConvertsPriceOrReturnsValue = (title: string, value: string | number) => {
  let convertString
  if (title === 'Цена') {
    convertString = value.toLocaleString() + ' ₽'
  } else if (title === 'Мощность двигателя (л.с.)') {
    convertString = value + ' л.с.'
  } else {
    convertString = value
  }
  return convertString
}

export const SelectedFilters: FC = () => {
  const props = useAVNConfig()
  const storeSelectedFilters = useStore($filterValues)
  const declaration = useStore($declaration)
  const dealersShowMoreCars = useStore($dealersShowMoreCars)
  const isLoadMoreCars = useStore($isLoadMoreCars)
  const relatedFilters = useStore($declarationWithRelatedFilters)
  const [filters, setFilters] = useState(declaration)

  let currentFilters: FilterItem[] = []

  // изначально нам приходит объект declaration и relatedFilters, которые одной длины или relatedFilters === 0, если данная функция отключена
  // при срабатывании зависимых фильтров, фильтра меняются, поэтому меняется их длина
  // мы проверяем длину первоначального объекта declaration и relatedFilter
  // если длина у них отличается и оба больше нуля, то тогда мы взаимодействуем с обновлёнными фильтрами в стейте, то есть с relatedFilters
  // если длина равна, взаимодействуем с declaration
  useEffect(() => {
    if (compareFiltersLength(declaration, relatedFilters)) {
      setFilters(relatedFilters)
    } else {
      setFilters(declaration)
    }
  }, [declaration, relatedFilters])

  if (!storeSelectedFilters || declaration === null) return null
  for (let key in storeSelectedFilters) {
    const section = filters?.sections.find((elem: { key: string }) => {
      return elem.key === key
    })
    if (
      section &&
      section?.groups?.length > 0 &&
      section?.groups[0]?.fields?.length > 0 &&
      objectHasProperty(section.groups[0].fields[0], 'min') &&
      objectHasProperty(section.groups[0].fields[0], 'max')
    ) {
      if (storeSelectedFilters[key][0] > Number(section.groups[0].fields[0].min)) {
        const valueMin: object = getObjectForFilterRange('min', storeSelectedFilters[key][0], section.title)
        currentFilters.push(createFilterItem(valueMin, section))
      }
      if (storeSelectedFilters[key][1] < Number(section.groups[0].fields[0].max)) {
        const valueMax: object = getObjectForFilterRange('max', storeSelectedFilters[key][1], section.title)
        currentFilters.push(createFilterItem(valueMax, section))
      }
    } else {
      storeSelectedFilters[key].forEach((elem) => {
        return currentFilters.push(createFilterItem(elem, section))
      })
    }
  }

  const deleteFilter = (key: any, sectionKey: string) => {
    if (key && key.range) {
      removeValues({ [sectionKey]: [key.range] })
    } else {
      removeValues({ [sectionKey]: [key] })
    }
  }

  const countFilterForButtonReset = currentFilters.filter((item) => item.key !== '').length
  return (
    <SelectedFilterInner>
      {Object.values(currentFilters).map((item) => {
        return objectHasProperty(item, 'key') && item.key !== '' ? (
          <Fragment key={item.key + item.label}>
            <PickedFilter data-for="label" data-tip={item?.tooltip}>
              {objectHasProperty(item, 'label') ? item.label : ''}
              <Delete
                onClick={() => {
                  if (props.showCarsNextDealer) {
                    setCarsNextDealer(dealersShowMoreCars.showCarsNextDealer, item.key)
                  }
                  deleteFilter(item.key, item.sectionKey)
                }}
              >
                <Icon name={'cancel'} size={1} />
              </Delete>
            </PickedFilter>
            {props.isShowTooltipInFilter && <Tooltip id="label" multiline={true} />}
          </Fragment>
        ) : null
      })}
      {countFilterForButtonReset >= 2 ? (
        <ResetSelectedFilter onClick={() => resetValues()}>{props.i18n.resetAllFilter}</ResetSelectedFilter>
      ) : null}
    </SelectedFilterInner>
  )
}

const SelectedFilterInner = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  gap: ${({}) => (getProjectName() === 'generic_v2' ? '4px' : '0')};
`

const PickedFilter = styled.div`
  ${({ theme }) => theme.typography.paragraph.p1};
  height: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return '27px'
    return theme.spacing[10]
  }};
  padding: ${({ theme }) => (getProjectName() === 'generic_v2' ? '4px 10px' : `0 ${theme.spacing[4]}`)};
  background: ${({ theme }) =>
    getProjectName() === 'generic_v2'
      ? '#2C2C2C'
      : getProjectName() === 'tank'
      ? theme.palette.grey[700]
      : theme.palette.white};
  font-size: ${({ theme }) => {
    if (getProjectName() === 'haval') return '1.4rem'
    if (getProjectName() === 'generic_v2') return '14px'
    return theme.spacing[4]
  }};
  color: ${({ theme }) => {
    if (getProjectName() === 'ora' || getProjectName() === 'benda') return theme.palette.black
    if (getProjectName() === 'generic_v2') return '#D9D9D9'
    return theme.palette.brand.primary
  }};
  display: flex;
  align-items: center;
  justify-content: ${({ theme }) => (getProjectName() === 'generic_v2' ? 'center' : 'flex-start')};
  gap: ${({ theme }) => (getProjectName() === 'generic_v2' ? '4px' : '0')};
  margin: ${({ theme }) => (getProjectName() === 'generic_v2' ? '0' : `0 ${theme.spacing[2]} ${theme.spacing[3]} 0`)};
  margin-top: ${({ theme }) => (getProjectName() === 'generic_v2' ? '6px' : '0')};
  border: ${({ theme }) => getProjectName() === 'tank' && ''};
  border-radius: ${({ theme }) =>
    getProjectName() === 'generic_v2' ? '100px' : getProjectName() === 'avito' ? '6px' : '0'};
`

const Delete = styled.div`
  width: ${({ theme }) => (getProjectName() === 'generic_v2' ? '14px' : theme.spacing[3])};
  height: ${({ theme }) => (getProjectName() === 'generic_v2' ? '14px' : theme.spacing[3])};
  margin-left: ${({ theme }) => theme.spacing[2]};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({}) => (getProjectName() === 'generic_v2' ? '#D9D9D9' : 'inherit')};
`

const ResetSelectedFilter = styled.div`
  ${({ theme }) => theme.typography.paragraph.p1};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => {
    if (getProjectName() === 'haval') return '1.4rem'
    return theme.spacing[4]
  }};
  padding: 0 ${({ theme }) => theme.spacing[2]};
  margin: 0 0 ${({ theme }) => theme.spacing[3]} 0;
  height: ${({ theme }) => theme.spacing[10]};
  cursor: pointer;
  color: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return '#D9D9D9'
    if (getProjectName() === 'tank') return theme.palette.grey[400]
    return theme.palette.black
  }};
  font-family: ${() => {
    if (getProjectName() === 'haval') return null
    return `BMWType, 'Roboto', 'Helvetica Neue', sans-serif`
  }};
`
